import { API } from "aws-amplify";
import { GraphQLQuery } from "@aws-amplify/api";
import { v4 as uuidv4, v5 as uuidv5 } from "uuid";
import {
  CreateAttachmentMutation,
  CreateTaxMutation,
  CreateTransactionMutation,
  CurrencyCode,
  Tax,
  TaxesByTransactionIdQuery,
  TaxesByTransactionIdQueryVariables,
  TransactionStatus,
  UpdateTaxMutation,
  UpdateTransactionMutation,
} from "../API";
import {
  createAttachment,
  createTax,
  createTransaction,
  updateTransaction,
  updateTax,
} from "../graphql/mutations";
import { taxesByTransactionId } from "../graphql/queries";
import { BookkeepingFull, OrganizationFull } from "../models";
import {
  getTaxAuthGroups,
  getTaxAuthGroupsRead,
  getTransactionAuthGroups,
  getTransactionAuthGroupsRead,
} from "./auth-groups";
import { Fx, resolveFx } from "./exchange-rate";
import { getLocalDate, queryList } from "../utils";

export type ReceiptTax = {
  taxRateId: string;
  amount: number | null;
  totalAmount: number | null;
};

export type ReceiptTransactionInput = {
  paidAt: string; // YYYY-MM-DD
  title: string;
  amount: number;
  currencyCode: CurrencyCode;
  status: TransactionStatus;
  bookkeepingYearId: string;
  bookingAccountId: string;
  // null clears a stored reference — same as the app's copyWith, which always
  // writes the temp value (globals.dart:424).
  transactionReferenceId: string | null;
  userId: string;
  fileId?: string;
  taxes: ReceiptTax[];
};

// Namespace for deriving child record ids from the transaction id (uuid v5 is
// deterministic): a retry recreates the same ids, so "already exists" becomes
// detectable instead of minting duplicates. Random constant, never change it.
const RECEIPT_ID_NAMESPACE = "7f3f0f7e-52fd-4c53-9b0a-6f2e6f1b9e11";

// AppSync surfaces DynamoDB's attribute_not_exists violation on create as
// this errorType — the record was committed by a previous attempt.
const isAlreadyExists = (error: unknown) =>
  Array.isArray((error as { errors?: { errorType?: string }[] })?.errors) &&
  (error as { errors: { errorType?: string }[] }).errors.some((e) =>
    (e.errorType ?? "").includes("ConditionalCheckFailedException")
  );

const taxInput = (
  tax: ReceiptTax,
  transactionId: string,
  userId: string,
  organization: OrganizationFull,
  bookkeeping: BookkeepingFull,
  isSingleTax: boolean
) => ({
  amount: tax.amount,
  // Single-tax invariant (see app saveTransaction): totalAmount only
  // meaningful for multi-tax splits; null for a single tax.
  totalAmount: isSingleTax ? null : tax.totalAmount,
  transactionId,
  taxRateId: tax.taxRateId,
  authUserId: userId,
  authGroups: getTaxAuthGroups(organization),
  authGroupsRead: getTaxAuthGroupsRead(bookkeeping.organizationId, bookkeeping.id),
});

// Mirrors app uploadTransaction/saveTransaction create path:
// taxes + attachment first, transaction last — the Bexio sync fires on the
// transaction's create stream and must find the taxes already there.
//
// Idempotent across retries: the caller may pass a stableId minted once per
// verify session. A commit whose response was lost ("Speichern
// fehlgeschlagen", user retries) then recreates the SAME ids — every child id
// derives from the transaction id via uuid v5 — and each "already exists"
// answer turns into an update with the current values instead of a duplicate.
//
// FX is resolved here rather than at the call sites so no write can forget it:
// a row saved with a null rate is read back as the raw foreign amount in the
// totals and the export (86c9pkyzj).
export const createReceiptTransaction = async (
  input: ReceiptTransactionInput,
  organization: OrganizationFull,
  bookkeeping: BookkeepingFull,
  baseCurrency: CurrencyCode,
  stableId?: string
): Promise<{ transactionId: string; fx: Fx }> => {
  const transactionId = stableId ?? uuidv4();

  const fx = await resolveFx(
    input.currencyCode,
    baseCurrency,
    input.amount,
    input.paidAt
  );

  await Promise.all(
    input.taxes.map(async (tax) => {
      const variables = {
        input: {
          id: uuidv5(
            `tax:${transactionId}:${tax.taxRateId}`,
            RECEIPT_ID_NAMESPACE
          ),
          ...taxInput(
            tax,
            transactionId,
            input.userId,
            organization,
            bookkeeping,
            input.taxes.length === 1
          ),
        },
      };
      try {
        await API.graphql<GraphQLQuery<CreateTaxMutation>>({
          query: createTax,
          variables,
        });
      } catch (error) {
        if (!isAlreadyExists(error)) throw error;
        // Committed by a previous attempt — write the current values in case
        // the user edited between attempts.
        await API.graphql<GraphQLQuery<UpdateTaxMutation>>({
          query: updateTax,
          variables: {
            input: {
              id: variables.input.id,
              amount: variables.input.amount,
              totalAmount: variables.input.totalAmount,
            },
          },
        });
      }
    })
  );

  if (input.fileId) {
    try {
      await API.graphql<GraphQLQuery<CreateAttachmentMutation>>({
        query: createAttachment,
        variables: {
          input: {
            id: uuidv5(
              `attachment:${transactionId}:${input.fileId}`,
              RECEIPT_ID_NAMESPACE
            ),
            key: input.fileId,
            transactionId,
            authUserId: input.userId,
            authGroups: getTransactionAuthGroups(organization),
            authGroupsRead: getTransactionAuthGroupsRead(
              bookkeeping.organizationId,
              bookkeeping.id
            ),
          },
        },
      });
    } catch (error) {
      // Same file, same key — nothing to update.
      if (!isAlreadyExists(error)) throw error;
    }
  }

  const transactionInput = {
    paidAt: input.paidAt,
    currencyCode: input.currencyCode,
    amount: input.amount,
    title: input.title,
    status: input.status,
    bookkeepingYearId: input.bookkeepingYearId,
    bookingAccountId: input.bookingAccountId,
    transactionReferenceId: input.transactionReferenceId,
    userId: input.userId,
    exchangeRate: fx.exchangeRate,
    baseCurrencyAmount: fx.baseCurrencyAmount,
  };
  try {
    await API.graphql<GraphQLQuery<CreateTransactionMutation>>({
      query: createTransaction,
      variables: {
        input: {
          id: transactionId,
          ...transactionInput,
          authGroups: getTransactionAuthGroups(organization),
          authGroupsRead: getTransactionAuthGroupsRead(
            bookkeeping.organizationId,
            bookkeeping.id
          ),
        },
      },
    });
  } catch (error) {
    if (!isAlreadyExists(error)) throw error;
    await API.graphql<GraphQLQuery<UpdateTransactionMutation>>({
      query: updateTransaction,
      variables: { input: { id: transactionId, ...transactionInput } },
    });
  }

  if (stableId) {
    // Retry reconciliation: a previous attempt may have committed taxes for
    // rates the user removed before retrying — the per-tax upsert above never
    // touches those, so they would stay active and over-count the VAT. Prune
    // every active tax whose id is not in the desired set. Only stable-id
    // sessions can retry, so only they pay the extra query.
    const desiredTaxIds = new Set(
      input.taxes.map((tax) =>
        uuidv5(`tax:${transactionId}:${tax.taxRateId}`, RECEIPT_ID_NAMESPACE)
      )
    );
    const existingTaxes = await queryList<
      Tax,
      TaxesByTransactionIdQueryVariables,
      TaxesByTransactionIdQuery
    >(taxesByTransactionId, { transactionId });
    await Promise.all(
      (existingTaxes ?? [])
        .filter((tax) => !tax.deletedAt && !desiredTaxIds.has(tax.id))
        .map((tax) =>
          API.graphql<GraphQLQuery<UpdateTaxMutation>>({
            query: updateTax,
            variables: {
              input: {
                id: tax.id,
                deletedAt: getLocalDate(new Date()).toISOString(),
              },
            },
          })
        )
    );
  }

  return { transactionId, fx };
};

// Verify path for an existing extracted transaction: diff taxes like the app
// (soft-delete removed, create added), then update the transaction itself.
//
// fxCaptureStale: the caller compares currency and amount against the stored
// row. An app-created extracted transaction arrives with FX already resolved,
// and that pair only describes this row while both are unchanged.
export const updateReceiptTransaction = async (
  transactionId: string,
  input: Omit<ReceiptTransactionInput, "fileId">,
  organization: OrganizationFull,
  bookkeeping: BookkeepingFull,
  baseCurrency: CurrencyCode,
  fxCaptureStale: boolean
): Promise<Fx> => {
  // Resolved before the tax writes, matching the create path: the fetch takes
  // up to ~1.3s live and has no business sitting inside the window between the
  // committed taxes and the transaction mutation. The live-refetch diff makes a
  // retry safe either way; this just keeps the partial-write window at the one
  // mutation that can actually fail.
  const fx = await resolveFx(
    input.currencyCode,
    baseCurrency,
    input.amount,
    input.paidAt
  );

  const isSingleTax = input.taxes.length === 1;
  const taxesNew = input.taxes.map((tax) => ({
    taxRateId: tax.taxRateId,
    amount: tax.amount,
    totalAmount: isSingleTax ? null : tax.totalAmount,
  }));

  // Read the taxes rather than take the caller's copy. The diff decides what to
  // create, so it has to run against what exists now: the modal's copy is the
  // one loaded when it opened, and this function's own tax writes commit before
  // the transaction update that can still throw. A retry then re-diffs against
  // a snapshot predating its own first attempt, does not see the tax it already
  // created, and creates it again — two active rows for one rate, VAT counted
  // twice. Costs one query on a path that already does several.
  const existingTaxes = await queryList<
    Tax,
    TaxesByTransactionIdQueryVariables,
    TaxesByTransactionIdQuery
  >(taxesByTransactionId, { transactionId });
  const activeTaxes = (existingTaxes ?? []).filter((tax) => !tax.deletedAt);

  const sameTax = (
    a: { taxRateId: string; amount?: number | null; totalAmount?: number | null },
    b: { taxRateId: string; amount?: number | null; totalAmount?: number | null }
  ) =>
    a.taxRateId === b.taxRateId &&
    (a.amount ?? null) === (b.amount ?? null) &&
    (a.totalAmount ?? null) === (b.totalAmount ?? null);

  const taxesToDelete = activeTaxes.filter(
    (tax) => !taxesNew.some((t) => sameTax(t, { ...tax, taxRateId: tax.taxRateId }))
  );
  const taxesToAdd = taxesNew.filter(
    (tax) => !activeTaxes.some((t) => sameTax(tax, { ...t, taxRateId: t.taxRateId }))
  );

  await Promise.all([
    ...taxesToDelete.map((tax) =>
      API.graphql<GraphQLQuery<UpdateTaxMutation>>({
        query: updateTax,
        variables: {
          input: {
            id: tax.id,
            deletedAt: getLocalDate(new Date()).toISOString(),
          },
        },
      })
    ),
    ...taxesToAdd.map((tax) =>
      API.graphql<GraphQLQuery<CreateTaxMutation>>({
        query: createTax,
        variables: {
          input: taxInput(
            tax,
            transactionId,
            input.userId,
            organization,
            bookkeeping,
            isSingleTax
          ),
        },
      })
    ),
  ]);

  // The stored pair is only worth keeping when it still describes this row AND
  // we failed to learn anything better. Anything else gets written:
  //   resolved          -> the new rate
  //   unquoted currency -> null, which is the honest answer (86c9pkyzj)
  //   back to base      -> null, clearing a rate that no longer applies
  // Writing the failure case too would let one BAZG hiccup during an unrelated
  // edit — fixing a title — erase FX that was already correct, turning a good
  // row into a 1000x mis-book. The app never overwrites a non-null pair either
  // (globals.dart:345).
  const keepCapturedFx = fx.lookupFailed && !fxCaptureStale;

  await API.graphql<GraphQLQuery<UpdateTransactionMutation>>({
    query: updateTransaction,
    variables: {
      input: {
        id: transactionId,
        paidAt: input.paidAt,
        currencyCode: input.currencyCode,
        amount: input.amount,
        title: input.title,
        status: input.status,
        bookkeepingYearId: input.bookkeepingYearId,
        bookingAccountId: input.bookingAccountId,
        transactionReferenceId: input.transactionReferenceId,
        userId: input.userId,
        ...(keepCapturedFx
          ? {}
          : {
              exchangeRate: fx.exchangeRate,
              baseCurrencyAmount: fx.baseCurrencyAmount,
            }),
      },
    },
  });

  return fx;
};
